/**
 * 载入与自动保存。
 * 启动：URL ?id= → GET /api/works/:id（失败回落 localStorage）；
 *       无 id 且已登录 → 弹模板选择（空白 / 模板 + 变量）→ 服务端创建；离线则直接本地空白。
 * 保存：doc 变化 debounce 3s → PUT；每次保存同时写 localStorage。
 */
import { useCallback, useEffect, useRef } from 'react';
import { message } from 'antd';
import { createWork, loadWork, type Work } from '@h5/schema';
import { api, localStore } from '../api/client';
import { getToken, requestLogin } from '../api/auth';
import { requestTemplateChoice } from '../templates/template-store';
import { useEditorStore } from '../store/store';
import { debounce } from '../utils/debounce';

function getQueryId(): string | null {
  return new URLSearchParams(window.location.search).get('id');
}

function setQueryId(id: string) {
  const url = new URL(window.location.href);
  url.searchParams.set('id', id);
  window.history.replaceState(null, '', url.toString());
}

export function usePersistence() {
  const loadedRef = useRef(false);

  const save = useCallback(async () => {
    const s = useEditorStore.getState();
    const doc = s.doc;
    const localId = s.ui.remoteId ?? doc.id;
    localStore.save(localId, doc);
    if (!s.ui.remoteId) {
      s.setSaveStatus('saved');
      return;
    }
    s.setSaveStatus('saving');
    try {
      await api.saveWork(s.ui.remoteId, doc);
      useEditorStore.getState().setSaveStatus('saved');
    } catch (err) {
      useEditorStore.getState().setSaveStatus('error');
      message.error(`保存失败：${(err as Error).message}（已保存到本地）`);
    }
  }, []);

  // 初始载入
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    const s = useEditorStore.getState();
    (async () => {
      const id = getQueryId();
      // 无 token 先弹登录；选择离线则后续请求会 401 并回落本地
      if (!getToken()) {
        const ok = await requestLogin();
        if (!ok) message.info('已进入离线模式，作品只保存在本地');
      }
      if (id) {
        let raw: unknown = null;
        try {
          raw = await api.getWork(id);
          s.setRemoteId(id);
        } catch (err) {
          raw = localStore.load(id);
          message.warning(raw ? `服务端加载失败，已使用本地副本：${(err as Error).message}` : `加载失败：${(err as Error).message}，已新建空白作品`);
        }
        if (raw) {
          try {
            s.setDoc(loadWork(raw));
            s.setSaveStatus('idle');
            return;
          } catch (err) {
            message.error(`文档解析失败：${(err as Error).message}，已新建空白作品`);
          }
        }
      }
      // 已登录才提供模板选择；离线直接空白
      const choice = getToken() ? await requestTemplateChoice() : { kind: 'blank' as const };
      if (choice.kind === 'template') {
        try {
          const res = await api.createWorkFromTemplate(choice.templateId, choice.vars, choice.title);
          const raw = res.doc ?? (await api.getWork(res.id));
          s.setDoc(loadWork(raw), { resetHistory: true });
          s.setRemoteId(res.id);
          setQueryId(res.id);
          s.setSaveStatus('idle');
          return;
        } catch (err) {
          message.error(`从模板创建失败：${(err as Error).message}，已改为新建空白作品`);
        }
      }
      const doc: Work = createWork();
      s.setDoc(doc);
      try {
        const res = await api.createWork(doc);
        s.setRemoteId(res.id);
        setQueryId(res.id);
      } catch (err) {
        message.warning(`服务端不可用，进入离线模式（仅本地保存）：${(err as Error).message}`);
        setQueryId(doc.id);
      }
      s.setSaveStatus('idle');
    })();
  }, []);

  // 自动保存
  useEffect(() => {
    const debounced = debounce(() => { void save(); }, 3000);
    const unsub = useEditorStore.subscribe((state, prev) => {
      if (state.doc !== prev.doc && !state.batch) debounced();
    });
    const onManual = () => { debounced.cancel(); void save(); };
    window.addEventListener('h5:save', onManual);
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      const st = useEditorStore.getState().ui.saveStatus;
      if (st === 'dirty' || st === 'saving') { debounced.flush(); e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      debounced.cancel();
      unsub();
      window.removeEventListener('h5:save', onManual);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [save]);

  return { save };
}
